import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles, Button, Select, IconButton, MenuItem, Checkbox, ListItemText, Grid, Card, CardContent, Typography, Box } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { injectIntl } from 'react-intl';
import * as API from 'services/api';
import { useSelector, useDispatch } from 'react-redux';
import { setLoading } from 'redux/actions/app';
import { get10LastLetters } from 'utils/validations'
import { CustomTable, RemoveModal } from 'components'
import OrderDetails from './detail';
import EmptyPage from 'components/EmptyPage';
import { appProfileSelector } from 'redux/selectors';
import { statues } from 'config';
import IconSave from "@material-ui/icons/Save";
import IconDelete from "@material-ui/icons/Delete";
import PropTypes from 'prop-types';
import { tableOptions } from 'pages/common';


const useStyles = makeStyles(theme => ({
  placeholder: {
    paddingLeft: theme.spacing(3)
  },
  table: {
    backgroundColor: 'white'
  },
  mobileCard: {
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[2],
    overflow: 'visible',
    wordBreak: 'break-word',
    '& .MuiCardContent-root': {
      padding: theme.spacing(2),
      '&:last-child': {
        paddingBottom: theme.spacing(2)
      }
    }
  },
  mobileRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  mobileActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  mobileField: {
    marginBottom: theme.spacing(1.5),
    '& .MuiTypography-caption': {
      color: theme.palette.text.secondary,
      display: 'block',
      marginBottom: theme.spacing(0.5)
    }
  },
  mobileSelect: {
    marginBottom: theme.spacing(1),
    '& .MuiSelect-root': {
      width: '100%'
    }
  },
  cartBtn: {
    color: "#05A969"
  },
}));

const Orders = ({
  intl: { formatMessage }
}) => {
  const classes = useStyles();
  const theme = useTheme();
  // only treat very small screens as mobile cards (max 600px)
  const isXs = useMediaQuery('(max-width:600px)');
  const dispatch = useDispatch();
  const [orders, setOrders] = useState([])
  const [openModal, setOpenModal] = useState(false);
  const profile = useSelector(appProfileSelector);
  const [openDelete, setOpenDelete] = useState();

  useEffect(() => {
    onLoad();
  }, []);
  const onLoad = () => {
    dispatch(setLoading(true));
    API.listUsers({})
      .then(res => {
        dispatch(setLoading(false));
        if (res.data && res.data.length > 0) {
          setOrders(res.data.filter(item => item.role == "user"))
        }
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
  }
  const onSave = (user) => e => {
    dispatch(setLoading(true));
    API.updateUser({
      id: user.id,
      permission: user.permission,
      status: user.status
    })
      .then(res => {
        dispatch(setLoading(false));
        if (res.code != 200) {
          onLoad();
        }
      })
      .catch(() => {
        dispatch(setLoading(false));
        onLoad();
      });
  }
  const onDelete = (user) => e => {
    setOpenDelete(user)
  }
  const deleteUser = (user) => {
    dispatch(setLoading(true));
    API.deleteUser({
      id: user.id
    })
      .then(res => {
        dispatch(setLoading(false));
        if (res.code == 200) {
          setOpenDelete();
          onLoad();
        }
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
  }

  let companies = [];
  if (profile.siret) {
    companies.push({ name: profile.company, siret: profile.siret, email: profile.clientEmail || profile.email })
  }
  if (profile.establishments && profile.establishments.length > 0) {
    companies.push(...profile.establishments)
  }

  const tableColumns = [
    {
      name: 'id',
      label: "ID",
      options: {
        display: true,
        sort: false,
        customBodyRender: (value) => get10LastLetters(value)
      }
    },
    {
      name: 'firstName',
      label: formatMessage({ id: "first_name" }),
      options: {
        display: true,
        sort: true
      }
    },
    {
      name: 'lastName',
      label: formatMessage({ id: "last_name" }),
      options: {
        display: true,
        sort: true
      }
    },
    {
      name: 'email',
      label: formatMessage({ id: "email" }),
      options: {
        display: true,
        sort: true
      }
    },
    ...companies.length > 0 ? [
      {
        name: 'sirets',
        label: "Etablissement secondaire",
        options: {
          display: true,
          sort: false,
          customBodyRender: (value, tableMeta) => renderSirets(tableMeta, orders, companies)
        }
      },
    ] : [],
    {
      name: 'domain',
      label: "Droits et visibilité",
      options: {
        display: true,
        sort: false,
        customBodyRender: useCallback((value, tableMeta) => {
          const id = tableMeta.rowData[0];
          const order = orders.find(item => item.id == id);
          let visibilities = [];
          if (!order.permission?.homepage?.hasOwnProperty("enable")) {
            if (!profile.clientPermission?.homepage?.hasOwnProperty("enable")) {
              if (profile.siret) {
                visibilities.push("formation");
              }
            } else if (profile?.clientPermission?.formation?.enable) {
              visibilities.push("formation");
            }
          } else {
            if (order?.permission?.inspection?.enable) {
              visibilities.push("inspection")
            }
            if (order?.permission?.formation?.enable) {
              visibilities.push("formation")
            }
          }
          return <Select
            multiple
            value={visibilities}
            onChange={e => {
              const newPermission = {
                ...order.permission,
                homepage: { enable: true },
                inspection: {
                  ...order.permission?.inspection,
                  enable: e.target.value.includes("inspection")
                },
                formation: {
                  ...order.permission?.inspection,
                  enable: e.target.value.includes("formation")
                },
              }
              setOrders(orders.map(el => el.id == tableMeta.rowData[0] ? { ...el, permission: newPermission } : el))
            }}
            renderValue={(selected) => selected.map(el => el.toUpperCase()).join(', ')}
            variant="outlined"
            style={{ minWidth: 100 }}
          >
            {
              profile.compteSEL &&
              <MenuItem value={"inspection"}>
                <Checkbox checked={visibilities.indexOf("inspection") > -1} />
                <ListItemText primary={"Inspection"} />
              </MenuItem>
            }
            <MenuItem value={"formation"}>
              <Checkbox checked={visibilities.indexOf("formation") > -1} />
              <ListItemText primary={"Formation"} />
            </MenuItem>
          </Select>
        }, [orders])
      }
    },
    {
      name: 'status',
      label: formatMessage({ id: "status" }),
      options: {
        display: true,
        sort: false,
        customBodyRender: useCallback((value, tableMeta) => {
          return <Select
            value={value}
            onChange={e => {
              setOrders(orders.map(el => el.id == tableMeta.rowData[0] ? { ...el, status: e.target.value } : el))
            }}
            variant="outlined"
            style={{ minWidth: 100 }}
          >
            {
              statues.map(item => <MenuItem key={item} value={item}>
                {formatMessage({ id: "user.status." + item })}
              </MenuItem>)
            }
          </Select>
        }, [orders])
      }
    },
    {
      name: 'actions',
      label: "ACTIONS",
      options: {
        display: true,
        sort: false,
        customBodyRender: useCallback((value, tableMeta) => {
          const id = tableMeta.rowData[0];
          const order = orders.find(item => item.id == id)
          return <Grid container>
            <IconButton size="small" className={classes.cartBtn}
              onClick={onSave(order)}
            ><IconSave /></IconButton>
            &nbsp;
            &nbsp;
            <IconButton size="small" className={classes.cartBtn} onClick={onDelete(order)}><IconDelete /></IconButton>
          </Grid>
        }, [orders])
      }
    }
  ];
  const renderSirets = useCallback((tableMeta, orders, companies) => {
    const id = tableMeta.rowData[0];
    const order = orders.find(item => item.id == id);
    const sirets = order?.permission?.sirets;
    return <Select
      multiple
      value={sirets}
      onChange={e => {
        const newPermission = {
          ...order.permission,
          sirets: e.target.value
        }
        setOrders(orders.map(el => el.id == tableMeta.rowData[0] ? { ...el, permission: newPermission } : el))
      }}
      renderValue={renderValue}
      variant="outlined"
      style={{ minWidth: 150, maxWidth: 600 }}
    >
      {companies.map((item) => (
        <MenuItem key={item._id} value={item.siret}>
          <Checkbox checked={sirets.indexOf(item.siret) > -1} />
          <ListItemText primary={item.name} />
        </MenuItem>
      ))}
    </Select>
  }, [])
  const renderValue = (selected) => {
    if (!selected || selected.length === 0) return '';
    const label = selected
      .map(el => (companies.find(item => item.siret == el)?.name || ''))
      .join(', ');

    return (
      <span
        style={{
          display: 'inline-block',
          maxWidth: 580, // a bit less than Select maxWidth to account for padding
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          verticalAlign: 'middle',
        }}
      >
        {label}
      </span>
    );
  };

  return (
    <div>
      {
        orders.length > 0 ?
          <>
            <Grid container justify='flex-end' style={{ marginBottom: 8 }}>
              <Button size='small' variant='contained' color="primary" className={classes.button} style={{ backgroundColor: '#05A969', borderRadius: 100 }}
                onClick={() => {
                  setOpenModal(true)
                }}
              >Inviter un utilisateur</Button>
            </Grid>
            {
              isXs ? (
                // mobile card list
                orders.map(user => (
                  <Card key={user.id} className={classes.mobileCard}>
                    <CardContent>
                      <Box className={classes.mobileRow}>
                        <div style={{ width: '100%' }}>
                          <Typography variant="subtitle1"><strong>{user.firstName} {user.lastName}</strong></Typography>
                          <Typography variant="body2" style={{ color: '#666', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</Typography>
                          <Typography variant="caption" style={{ color: '#999' }}>{get10LastLetters(user.id)}</Typography>
                        </div>
                      </Box>
                      
                      {/* Établissement secondaire */}
                      {companies.length > 0 && (
                        <div className={classes.mobileField}>
                          <Typography variant="caption">Établissement secondaire</Typography>
                          <Select
                            multiple
                            fullWidth
                            value={user.permission?.sirets || []}
                            onChange={e => {
                              const newPermission = {
                                ...user.permission,
                                sirets: e.target.value
                              }
                              setOrders(orders.map(el => el.id == user.id ? { ...el, permission: newPermission } : el))
                            }}
                            renderValue={renderValue}
                            variant="outlined"
                            className={classes.mobileSelect}
                          >
                            {companies.map((item) => (
                              <MenuItem key={item._id} value={item.siret}>
                                <Checkbox checked={(user.permission?.sirets || []).indexOf(item.siret) > -1} />
                                <ListItemText primary={item.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                      )}
                      
                      {/* Droits et visibilité */}
                      <div className={classes.mobileField}>
                        <Typography variant="caption">Droits et visibilité</Typography>
                        <Select
                          multiple
                          fullWidth
                          value={(() => {
                            let visibilities = [];
                            if (!user.permission?.homepage?.hasOwnProperty("enable")) {
                              if (!profile.clientPermission?.homepage?.hasOwnProperty("enable")) {
                                if (profile.siret) {
                                  visibilities.push("formation");
                                }
                              } else if (profile?.clientPermission?.formation?.enable) {
                                visibilities.push("formation");
                              }
                            } else {
                              if (user?.permission?.inspection?.enable) {
                                visibilities.push("inspection")
                              }
                              if (user?.permission?.formation?.enable) {
                                visibilities.push("formation")
                              }
                            }
                            return visibilities;
                          })()}
                          onChange={e => {
                            const newPermission = {
                              ...user.permission,
                              homepage: { enable: true },
                              inspection: {
                                ...user.permission?.inspection,
                                enable: e.target.value.includes("inspection")
                              },
                              formation: {
                                ...user.permission?.inspection,
                                enable: e.target.value.includes("formation")
                              },
                            }
                            setOrders(orders.map(el => el.id == user.id ? { ...el, permission: newPermission } : el))
                          }}
                          renderValue={(selected) => selected.map(el => el.toUpperCase()).join(', ')}
                          variant="outlined"
                          className={classes.mobileSelect}
                        >
                          {profile.compteSEL && (
                            <MenuItem value={"inspection"}>
                              <Checkbox checked={user?.permission?.inspection?.enable} />
                              <ListItemText primary={"Inspection"} />
                            </MenuItem>
                          )}
                          <MenuItem value={"formation"}>
                            <Checkbox checked={user?.permission?.formation?.enable} />
                            <ListItemText primary={"Formation"} />
                          </MenuItem>
                        </Select>
                      </div>
                      
                      {/* Status and Actions */}
                      <div className={classes.mobileField}>
                        <Typography variant="caption">Status</Typography>
                        <Box display="flex" alignItems="center" mt={1}>
                          <Select
                            fullWidth
                            value={user.status}
                            onChange={e => setOrders(orders.map(el => el.id == user.id ? { ...el, status: e.target.value } : el))}
                            variant="outlined"
                            style={{ marginRight: theme.spacing(1) }}
                          >
                            {
                              statues.map(item => <MenuItem key={item} value={item}>
                                {formatMessage({ id: "user.status." + item })}
                              </MenuItem>)
                            }
                          </Select>
                          <div className={classes.mobileActions}>
                            <IconButton size="small" className={classes.cartBtn} onClick={onSave(user)}>
                              <span style={{ fontSize: 14 }}>💾</span>
                            </IconButton>
                            <IconButton size="small" className={classes.cartBtn} onClick={onDelete(user)}>
                              <span style={{ fontSize: 14 }}>🗑️</span>
                            </IconButton>
                          </div>
                        </Box>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <CustomTable
                  columns={tableColumns}
                  data={orders}
                  options={{
                    ...tableOptions(),
                  }}
                />
              )
            }
          </> :
          <EmptyPage title="Vous êtes le seul utilisateur à ce jour, invitez vos collègues !" buttonLabel="Inviter un utilisateur"
            onClick={() => {
              setOpenModal(true)
            }}
          />
      }
      {
        openModal &&
        <OrderDetails
          open={openModal}
          onClose={(refresh) => {
            if (refresh) onLoad();
            setOpenModal(false)
          }}
        />
      }
      {openDelete && (
        <RemoveModal
          visible={!!openDelete}
          onClose={() => setOpenDelete()}
          onConfirm={() => {
            deleteUser(openDelete)
          }}
          loading={false}
          title="Suppression d’un utilisateur"
          description={"Souhaitez-vous supprimer cet utilisateur ?"}
        />
      )}
    </div>
  );
};
Orders.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  })
};
export default injectIntl(Orders);
